import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Image,
  GridItem,
  Heading,
  Text,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { IoEyeOutline as EyeIcon } from 'react-icons/io5';
import { MdOutlineModeEditOutline as EditIcon } from 'react-icons/md';
import { TopLink } from '..';
import { useState, useRef } from 'react';
import {
  openAtom,
  modalAtom,
  notificationAtom,
  openNAtom,
} from '../../../../context';
import { useAtom } from 'jotai';
import ViewSetting from '../Modal/ViewSetting';
import { apiCall } from '../../../../utils/apiCall';
import apiCallFormData from '../../../../utils/apiFormDataCall';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Editor } from '../../../../components';
import HTMLReactParser from 'html-react-parser';
import { Loader } from '../../../../customs';

type TranscriptDataType = {
  Id: number;
  Code: string;
  Body: string;
};



const TranscriptTemplate = () => {
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);

  // ✅ Fetch Template
  const {
    isLoading: templateLoading,
    data: transcriptTemplate,
  } = useQuery<TranscriptDataType | null>({
    queryKey: ['transcript-template'],
    queryFn: async () => {
      const res = await apiCall().get(
        '/exam/setup/exam/GetExamTranscriptTemplate'
      );
      return res?.data?.Data ?? null;
    },
  });

  // ✅ Fetch Transcript Details (dynamic data)
  const {
    data: transcriptDetail,
    isLoading: detailLoading,
  } = useQuery({
    queryKey: ['transcript-details'],
    queryFn: async () => {
      const res = await apiCall().post(
        `/Exam/transcriptTemplateDetails`
      );
      return res?.data?.Data ?? null;
    },
    enabled: true,
  });

  // ✅ Inject dynamic values into template
  const injectTranscriptData = (
    template: string,
    details: any
  ) => {
    if (!template || !details) return template;

    let updatedTemplate = template;

    Object.keys(details).forEach((key) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      updatedTemplate = updatedTemplate.replace(
        regex,
        details[key] ?? ''
      );
    });

    return updatedTemplate;
  };

  const mergedBody =
    transcriptTemplate?.Body && transcriptDetail
      ? injectTranscriptData(transcriptTemplate.Body, transcriptDetail)
      : transcriptTemplate?.Body;

  const openView = (transcript: TranscriptDataType) => {
    set_modal({
      header: 'View Transcript Template',
      content: <ViewType transcript={transcript} />,
    });
    set_open(true);
  };

  const openEdit = (transcript: TranscriptDataType) => {
    set_modal({
      header: 'Edit Transcript Template',
      content: <EditType transcript={transcript} />,
    });
    set_open(true);
  };

  if (templateLoading || detailLoading) return <Loader />;

  return (
    <Box>
      <TopLink />

      <Flex justify="space-between" align="center" mt="20px">
        <Heading fontSize="1.5rem" color="var(--dark-green-color)">
          Transcript Template Editor
        </Heading>
      </Flex>

      <Flex mt="25px" flexDir="column" gap="5px">
        <Heading fontSize="1rem">Transcript Template</Heading>
        <Text fontSize=".8rem">
          This template is used to generate official student transcripts
        </Text>
      </Flex>

      <Grid templateColumns="1fr" gap="20px" mt="20px">
        {transcriptTemplate ? (
          <Card p="20px" display="flex" justify="space-between">
            <Flex flexDir="column" flex="1">
              <Box>
                {mergedBody
                  ? HTMLReactParser(mergedBody)
                  : 'No content'}
              </Box>
            </Flex>

            <Flex gap="12px" fontSize="1.3rem" align="flex-start">
              <EyeIcon
                onClick={() => openView(transcriptTemplate)}
                style={{ cursor: 'pointer' }}
                title="View template"
              />
              <EditIcon
                onClick={() => openEdit(transcriptTemplate)}
                style={{ cursor: 'pointer' }}
                title="Edit template"
              />
            </Flex>
          </Card>
        ) : (
          <Text>No transcript template found.</Text>
        )}
      </Grid>
    </Box>
  );
};



const StylePaginateBox = styled.div`
  width: 100%;
  & > ul {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin: 0;
    margin-top: 10px;
  } 
  & > ul > li {
    display: inline;
    margin: 0 5px;
    padding: 2px 5px;
    border-radius: 3px;
    background-color: transparent;
    color: var(--dark-green-color);
  }
  & > ul > li.selected {
    background-color: var(--highlight-paginate-background);
    color: var(--highlight-paginate-color);
  }
  & > ul > li:first-of-type,
  & > ul > li:last-of-type {
    font-weight: bold;
  }
`;

const ViewType = ({ transcript }: { transcript: TranscriptDataType }) => {
  const pdfRef = useRef<any>(null);
  return (
    <Flex flexDir="column" gap="12px">
      {HTMLReactParser(transcript.Body)}
    </Flex>
  );
};


const EditType = ({ transcript }: { transcript: TranscriptDataType }) => {
  const [value, set_value] = useState(transcript.Body);
  const queryClient = useQueryClient();
  const [, set_open] = useAtom(openAtom);
  const [, set_notification] = useAtom(notificationAtom);
  const [, set_open_notification] = useAtom(openNAtom);

  const { mutate: editTemplate, isLoading } = useMutation({
    mutationFn: async (data: FormData) => {
      return apiCallFormData().post(
        '/exam/setup/exam/CreateUpdateExamTranscriptTemplate',
        data
      );
    },
    onSuccess: (res: any) => {
      set_notification({
        type: 'success',
        message: res?.data?.Message || 'Template updated successfully',
      });
      queryClient.invalidateQueries(['transcript-template']);
      set_open(false);
      set_open_notification(true);
    },
    onError: (err: any) => {
      set_notification({
        type: 'error',
        message: err?.response?.data?.Message || 'Update failed',
      });
      set_open_notification(true);
    },
  });

  const handleSubmit = () => {
    if (!value.trim()) {
      set_notification({
        type: 'error',
        message: 'Body cannot be empty',
      });
      set_open_notification(true);
      return;
    }

    const formData = new FormData();
    formData.append('Id', String(transcript.Id)); 
    formData.append('Body', value);               

    editTemplate(formData);
  };

  return (
    <Flex flexDir="column" gap="12px">
      <Editor value={value} onChange={set_value} />

      <Flex justify="flex-end">
        <Button
          onClick={handleSubmit}
          bg="var(--dark-green-color)"
          color="white"
          isLoading={isLoading}
        >
          Submit
        </Button>
      </Flex>
    </Flex>
  );
};


export default TranscriptTemplate;